import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation, Trans } from 'react-i18next';
import { apiFetch } from '@/lib/api';
import { blacklistListSchema, blacklistAddSchema } from '@/schemas/blacklist';
import type { BlacklistEntry, BlacklistAdd, BlacklistList } from '@/schemas/blacklist';
import BlacklistTable from '@/components/app/blacklist-table';
import AddBlacklistDialog from '@/components/app/add-blacklist-dialog';
import ConfirmDestructiveDialog from '@/components/app/confirm-destructive-dialog';

// The endpoint returns the whole list as a bare array (no pagination envelope),
// so the page paginates it locally. The `page`/`limit` query params are ignored
// by the server anyway (`GET /api/blacklist` has no such filters).
async function fetchBlacklist(): Promise<BlacklistList> {
  const resp = await apiFetch('/blacklist');
  const json = await resp.json();
  return blacklistListSchema.parse(json);
}

async function addBlacklist(data: BlacklistAdd) {
  const resp = await apiFetch('/blacklist', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return resp.json();
}

async function removeBlacklist(serial: string) {
  const resp = await apiFetch(`/blacklist/${encodeURIComponent(serial)}`, {
    method: 'DELETE',
  });
  return resp.json();
}

export default function BlacklistPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const page = Math.max(1, Number(searchParams.get('page') ?? '1'));
  const limit = Math.max(1, Number(searchParams.get('limit') ?? '10'));

  const [addOpen, setAddOpen] = React.useState(false);
  const [removeEntry, setRemoveEntry] = React.useState<BlacklistEntry | null>(null);
  const [addError, setAddError] = React.useState<string | null>(null);

  const query = useQuery({
    queryKey: ['blacklist'],
    queryFn: fetchBlacklist,
    staleTime: 30_000,
  });

  const addMutation = useMutation({
    mutationFn: addBlacklist,
    onMutate: async (variables) => {
      await queryClient.cancelQueries({ queryKey: ['blacklist'] });
      const prev = queryClient.getQueryData(['blacklist']);
      queryClient.setQueryData(['blacklist'], (old: unknown) => {
        const parsed = blacklistListSchema.safeParse(old);
        if (!parsed.success) return old;
        const entry = blacklistAddSchema.parse(variables);
        const optimistic: BlacklistEntry = {
          id: `pending-${Date.now()}`,
          serial: entry.serial,
          reason: entry.reason,
          licenseId: null,
          createdAt: new Date().toISOString(),
        };
        return [optimistic, ...parsed.data];
      });
      return { prev };
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['blacklist'], context.prev);
      }
      setAddError(t('blacklist.toastAddError'));
    },
    onSuccess: () => {
      setAddOpen(false);
      setAddError(null);
      toast.success(t('blacklist.toastAdded'));
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (serial: string) => removeBlacklist(serial),
    onMutate: async (serial) => {
      await queryClient.cancelQueries({ queryKey: ['blacklist'] });
      const prev = queryClient.getQueryData(['blacklist']);
      queryClient.setQueryData(['blacklist'], (old: unknown) => {
        const parsed = blacklistListSchema.safeParse(old);
        if (!parsed.success) return old;
        return parsed.data.filter((e) => e.serial !== serial);
      });
      return { prev };
    },
    onError: (_err, _serial, context) => {
      if (context?.prev) {
        queryClient.setQueryData(['blacklist'], context.prev);
      }
      toast.error(t('blacklist.toastRemoveError'));
    },
    onSuccess: () => {
      setRemoveEntry(null);
      toast.success(t('blacklist.toastRemoved'));
      queryClient.invalidateQueries({ queryKey: ['blacklist'] });
    },
  });

  const handleAdd = (values: BlacklistAdd) => {
    setAddError(null);
    addMutation.mutate(values);
  };

  const handleRemove = () => {
    if (removeEntry) {
      removeMutation.mutate(removeEntry.serial);
    }
  };

  const updatePage = (p: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  };

  const updateLimit = (l: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('limit', String(l));
      next.set('page', '1');
      return next;
    });
  };

  const entries = query.data ?? [];
  const total = entries.length;
  const pageItems = entries.slice((page - 1) * limit, page * limit);
  const isEmpty = !query.isLoading && total === 0;
  const isError = query.isError;

  return (
    <div className="mx-auto w-full max-w-[1120px]">
      {/* Page head */}
      <div className="flex items-start justify-between gap-8">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground">
            {t('blacklist.title')}
          </h1>
          <p className="mt-2.5 max-w-[52ch] font-serif text-[17.5px] italic leading-relaxed text-muted-foreground">
            {t('blacklist.subtitle')}
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="mt-2.5 shrink-0">
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('blacklist.addEntry')}
        </Button>
      </div>

      <div className="mt-8">
        {isError ? (
          <div className="flex items-center justify-between rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{t('blacklist.loadError')}</p>
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        ) : isEmpty ? (
          <div className="flex h-[50vh] flex-col items-center justify-center gap-4">
            <h2 className="font-serif text-2xl italic tracking-tight text-foreground">
              {t('blacklist.empty')}
            </h2>
            <p className="max-w-[44ch] text-center text-[14.5px] text-muted-foreground">
              {t('blacklist.emptyHint')}
            </p>
            <Button onClick={() => setAddOpen(true)} className="mt-1">
              {t('blacklist.emptyCta')}
            </Button>
          </div>
        ) : (
          <BlacklistTable
            data={pageItems}
            total={total}
            isLoading={query.isLoading}
            page={page}
            limit={limit}
            onPageChange={updatePage}
            onLimitChange={updateLimit}
            onRemove={setRemoveEntry}
          />
        )}
      </div>

      <AddBlacklistDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={handleAdd}
        isLoading={addMutation.isPending}
        serverError={addError}
      />

      {removeEntry && (
        <ConfirmDestructiveDialog
          open={!!removeEntry}
          onOpenChange={(open) => {
            if (!open) setRemoveEntry(null);
          }}
          title={t('blacklist.removeDialog.title')}
          description={
            <Trans
              i18nKey="blacklist.removeDialog.description"
              values={{ serial: removeEntry.serial }}
              components={{ strong: <strong /> }}
            />
          }
          confirmValue={removeEntry.serial}
          confirmLabel={t('blacklist.table.remove')}
          onConfirm={handleRemove}
          isLoading={removeMutation.isPending}
        />
      )}
    </div>
  );
}

export const handle = { crumb: 'Blacklist' };
